import React, {FC, useMemo} from 'react';
import {Typography} from '@mui/material';
import {MaterialReactTable, MRT_ColumnDef as MRTColumnDef} from 'material-react-table';
import ClaimStatus from '../ClaimStatus';
import {useNavigate} from 'react-router-dom';
import {
  parseReplacementNumbers,
  parseReplacementNames,
  parseReplacementPositions,
  parseReplacementTypes,
} from '../../../helpers/claim_helper';
import {Badge} from 'react-bootstrap';
import {CsvExportButton} from '../../Common/CsvExportButton';
import moment from 'moment/moment';
import {parsePrinterType} from '../../../helpers/printer_helper';

interface ClaimListProps {
  isLoading: boolean;
  claims: Claim[];
}

export const ClaimList: FC<ClaimListProps> = ({isLoading, claims}) => {
  const navigate = useNavigate();

  const columns = useMemo<MRTColumnDef<Claim>[]>(() => [
    {
      header: '#FR',
      accessorKey: 'fault_report_no',
    },
    {
      header: 'SN',
      accessorKey: 'printer_serial_no',
    },
    {
      header: 'Model',
      accessorKey: 'printer_type',
      accessorFn: (row) => row.printer ? parsePrinterType(row.printer) : '',
    },
    {
      header: 'Dealer',
      accessorKey: 'dealer_name',
    },
    {
      header: 'Customer',
      accessorKey: 'customer_name',
    },
    {
      header: 'Claim',
      accessorKey: 'date_of_visit',
      accessorFn: (row) =>
        row.date_of_visit ?
          moment(row.date_of_visit).format('DD-MM-YYYY') :
          '',
    },
    {
      header: 'Input',
      accessorKey: 'created_at',
      accessorFn: (row) =>
        row.created_at ?
          moment(row.created_at).format('DD-MM-YYYY') :
          '',
    },
    {
      header: 'Replacement',
      accessorKey: 'replacement_type',
    },
    {
      header: 'Position',
      accessorKey: 'ph_positions',
    },
    {
      header: 'Type',
      accessorKey: 'types',
    },
    {
      header: 'Number',
      accessorKey: 'replacement_numbers',
    },
    {
      header: 'Name',
      accessorKey: 'replacement_names',
    },
    {
      header: 'Claim Status',
      accessorKey: 'status',
      accessorFn: (row) => row.status,
      Cell: ({row}) => {
        const claim = row.original as Claim;
        return (
          <ClaimStatus
            status={claim.status}
            final_check_status={claim.final_check_status}
            internal_check_status={claim.internal_check_status}
          />
        );
      },
    },
    {
      header: 'Comments',
      accessorKey: 'comments_count',
      accessorFn: (row) => row.comments_count,
      Cell: ({cell}) =>
        cell.getValue<number>() > 0 ? (
          <Badge pill variant="success" className="badge-soft-success">
            {cell.getValue<number>()}
          </Badge>
        ) : (
          <Badge pill variant="light">{cell.getValue<number>()}</Badge>
        ),
    },
  ], []);

  const data = useMemo(() => claims.map((claim) => ({
    ...claim,
    replacement_type: claim.replacement_type === 2 ? 'Other part' : 'Print head',
    replacement_numbers: parseReplacementNumbers(claim),
    replacement_names: parseReplacementNames(claim),
    ph_positions: parseReplacementPositions(claim),
    types: parseReplacementTypes(claim),
    dealer_name: claim.dealer ? claim.dealer.name : '',
    customer_name: claim.customer ? claim.customer.name : '',
  })), [claims]);

  return (
    <MaterialReactTable
      columns={columns}
      data={isLoading ? [] : data}
      enableSorting
      enablePagination
      enableGlobalFilter
      state={{isLoading}}
      muiTablePaperProps={{
        elevation: 0,
      }}
      muiTableBodyRowProps={({row}) => ({
        onClick: () => navigate(`/claim/${row.original.id}`),
        style: {cursor: 'pointer'},
      })}
      renderTopToolbarCustomActions={({table}) => {
        return (
          <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
            <Typography variant="h6">Claims</Typography>
            <CsvExportButton table={table} filename={'claims'}/>
          </div>
        );
      }}
    />
  );
};
